import api from "./axiosInstance";
import apiRoutes from "./api_route";

// The backend sometimes wraps the real payload in a nested { data: {...} }
// object (and may include an "error" field) instead of returning a flat
// response. Flatten any nested data up to the top level so callers can
// always read fields like status/token/meetingDate directly.
function flattenPayload(res) {
  if (!res || typeof res !== "object") return res || {};
  let flat = { ...res };
  let inner = res.data;
  for (let depth = 0; depth < 2 && inner && typeof inner === "object"; depth++) {
    flat = { ...flat, ...inner };
    inner = inner.data;
  }
  return flat;
}

// `externalToken` is the meeting-scoped JWT minted by admin/join, checkJoinStatus
// or host/room-token. The backend's externalTokenVerify reads it from the
// Authorization header and rejects with 401 when neither it nor a login session
// is present, so it must be forwarded explicitly rather than left in state.
export const getTokenApi = async (
  identity,
  roomName,
  externalToken,
  roomKind,
  phoneNumber,
) => {
  const response = await api.post(
    apiRoutes.token.generate,
    {
      identity,
      room: roomName,
      // The backend resolves the admission entry by identity OR phone number
      // and mints the Twilio grant from entry.id || entry.phoneNumber, so the
      // phone must ride along when identity alone does not match.
      ...(phoneNumber ? { phoneNumber } : {}),
      // Officials use this to pick a specific room (meeting vs lobby) so the
      // same host can hold both rooms in separate tabs without colliding.
      ...(roomKind ? { roomKind } : {}),
    },
    externalToken
      ? {
          externalToken,
          // Tag the session key (lobby rooms share the parent meeting code) so
          // a 401 on this call triggers the participant token refresh.
          meetingCode: String(roomName).replace(/-lobby$/, ""),
        }
      : { meetingCode: String(roomName).replace(/-lobby$/, "") },
  );
  return response.data;
};

export const encryptMeetingValuesApi = async (payload) => {
  const response = await api.post(apiRoutes.token.encrypt, { payload });
  return response.data;
};

export const decryptMeetingValuesApi = async (token) => {
  const response = await api.post(apiRoutes.token.decrypt, { token });
  return response.data;
};

export const fetchRoomsApi = async (userId) => {
  const response = await api.get(apiRoutes.rooms.list, {
    params: { userId },
  });
  return response.data;
};

export const startMeetingApi = async (meetingData) => {
  const response = await api.post(apiRoutes.rooms.start, meetingData);
  return response.data;
};

export const completeRoomApi = async (roomId, userId) => {
  const response = await api.post(apiRoutes.rooms.complete, { roomId, userId });
  return response.data;
};

export const joinMeetingApi = async ({
  roomCode,
  roomPassword,
  phoneNumber,
  identity,
}) => {
  const response = await api.post(apiRoutes.rooms.joinRequest, {
    roomCode,
    roomPassword,
    phoneNumber,
    identity,
  });
  return response.data;
};

export const checkJoinStatusApi = async (
  meetingCode,
  phoneNumber,
  identity,
) => {
  // Identity must always travel: a phone-less entry (refresh / direct link)
  // still needs its admission record and the external token that lets the
  // socket authenticate, otherwise the participant never receives the
  // live "approved" event and only a manual refresh moves them.
  const body = { meetingCode };
  if (phoneNumber) body.phoneNumber = phoneNumber;
  if (identity) body.identity = identity;
  const response = await api.post(apiRoutes.rooms.joinStatus, body);
  return flattenPayload(response.data);
};

export const syncMeetingStateApi = async (
  meetingCode,
  { lastEventId, identity, phoneNumber } = {},
) => {
  const response = await api.post(apiRoutes.rooms.sync, {
    meetingCode,
    ...(lastEventId ? { lastEventId } : {}),
    ...(identity ? { identity } : {}),
    ...(phoneNumber ? { phoneNumber } : {}),
  });
  return flattenPayload(response.data);
};

export const getWaitingParticipantsApi = async (meetingCode) => {
  const response = await api.post(apiRoutes.rooms.waiting, { meetingCode });
  return flattenPayload(response.data);
};

export const approveParticipantApi = async (roomCode, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.approve, {
    roomCode,
    phoneNumber,
  });
  return response.data;
};

export const removeParticipantApi = async (roomCode, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.remove, {
    roomCode,
    phoneNumber,
  });
  return response.data;
};

export const leaveMeetingApi = async (roomCode, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.leave, {
    roomCode,
    phoneNumber,
  });
  return response.data;
};

// Dual-room endpoints. `approveParticipantApi` is intentionally left as the
// lobby-to-meeting promotion path (the backend /approve handler).
export const admitParticipantApi = async (roomCode, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.admit, {
    roomCode,
    phoneNumber,
  });
  return response.data;
};

export const moveToLobbyApi = async (roomCode, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.moveToLobby, {
    roomCode,
    phoneNumber,
  });
  return response.data;
};

// Two-step admission: participant already in the lobby; admin's "Move to meeting".
export const promoteToMeetingApi = async (roomCode, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.promoteToMeeting, {
    roomCode,
    phoneNumber,
  });
  return response.data;
};

export const getHostRoomTokenApi = async (roomCode, roomKind) => {
  const response = await api.post(apiRoutes.rooms.hostRoomToken, {
    roomCode,
    roomKind,
  });
  return response.data;
};

// Server-observed presence advisory (plan 5.3). Mirrors the Twilio webhook so
// a lobby/meeting connect registers even when webhooks cannot reach the server
// (local dev): the backend marks the entry stage "lobby"/presence in-lobby and
// broadcasts the host's drawer update.
export const lobbyConnectedApi = async (roomCode, identity, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.lobbyConnected, {
    roomCode,
    identity,
    ...(phoneNumber ? { phoneNumber } : {}),
  });
  return response.data;
};

export const meetingConnectedApi = async (roomCode, identity, phoneNumber) => {
  const response = await api.post(apiRoutes.rooms.meetingConnected, {
    roomCode,
    identity,
    ...(phoneNumber ? { phoneNumber } : {}),
  });
  return response.data;
};

export const adminJoinMeetingApi = async ({
  roomCode,
  roomPassword,
  identity,
  phoneNumber,
}) => {
  const response = await api.post(apiRoutes.rooms.adminJoin, {
    roomCode,
    roomPassword,
    identity,
    phoneNumber,
  });
  return response.data;
};
